import React, { useMemo, useState } from 'react';
import { CheckCircle, ExternalLink, Info, PanelRightOpen, PlayCircle, ThumbsDown, ThumbsUp } from 'lucide-react';
import { components, palette } from '../../utils/designTokens';
import {
  getTableCellColor,
  getTableRowStyle,
  tableGridHeaderClassName,
  tablePanelTitleClassName,
} from '../TablePrimitives';

// Signal | Where | Count | Evidence | Root Cause | Latest | Traces | Feedback | Status
const gridTemplate = 'minmax(160px, 0.85fr) minmax(115px, 0.65fr) 66px minmax(210px, 1.65fr) minmax(230px, 1.85fr) 64px 46px 86px 100px';
const gridGap = '14px';

const formatCount = (value) => Number(value || 0).toLocaleString();

const actionStyle = (tone) => {
  const color = tone === 'run' ? palette.status.warning : palette.accents.primary.base;
  return {
    color,
    borderColor: `${color}55`,
    backgroundColor: `${color}12`,
  };
};

const feedbackStyle = (active, color) => ({
  color,
  borderColor: active ? color : `${color}55`,
  backgroundColor: active ? `${color}22` : `${color}12`,
});

const RcaInlineAction = ({ row, onViewRca, onRunRca }) => {
  if (!row?.actionType || row.actionType === 'view_traces') return null;
  const config = row.actionType === 'view_rca'
    ? { label: 'View RCA', icon: PanelRightOpen, tone: 'view', onClick: onViewRca }
    : { label: 'Run RCA', icon: PlayCircle, tone: 'run', onClick: onRunRca };
  const Icon = config.icon;

  return (
    <button
      type="button"
      aria-label={config.label}
      title={config.label}
      className="inline-flex shrink-0 items-center justify-center rounded border p-1 transition-opacity hover:opacity-85"
      style={actionStyle(config.tone)}
      onClick={(event) => {
        event.preventDefault();
        event.stopPropagation();
        config.onClick?.(row, event);
      }}
    >
      <Icon size={12} />
    </button>
  );
};

const TracesButton = ({ row, onViewTraces }) => {
  if (!row?.evidenceLink) return <span className="text-xs" style={{ color: getTableCellColor('muted') }}>-</span>;

  return (
    <button
      type="button"
      aria-label="Open traces"
      title="Open traces"
      className="inline-flex items-center justify-center rounded border p-1 transition-opacity hover:opacity-85"
      style={actionStyle('view')}
      onClick={(event) => {
        event.preventDefault();
        event.stopPropagation();
        onViewTraces?.(row, event);
      }}
    >
      <ExternalLink size={12} />
    </button>
  );
};

const ProblemFeedbackButtons = ({ row, onProblemFeedback, busyActionKey }) => {
  const problemId = row?.problem?.problem_id || row?.group?.problem_id || '';
  const feedback = row?.problem?.feedback || {};
  const currentVote = feedback.current_user_vote || '';
  const thumbsUpActive = currentVote === 'problem_thumbs_up' || currentVote === 'thumbs_up';
  const thumbsDownActive = currentVote === 'problem_thumbs_down' || currentVote === 'thumbs_down';
  const feedbackBusy = busyActionKey === `${row?.id}:feedback`;
  if (!problemId) return <span className="text-xs" style={{ color: getTableCellColor('muted') }}>-</span>;

  return (
    <div className="flex justify-end gap-1">
      <button
        type="button"
        title="Problem grouping is useful"
        aria-label="Problem grouping is useful"
        disabled={feedbackBusy}
        className="inline-flex items-center justify-center gap-1 rounded border px-1.5 py-1 text-[11px] font-medium transition-opacity hover:opacity-85 disabled:opacity-50"
        style={feedbackStyle(thumbsUpActive, palette.status.success)}
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          onProblemFeedback?.(row, 'thumbs_up');
        }}
      >
        <ThumbsUp size={12} />
        <span>{feedback.thumbs_up || 0}</span>
      </button>
      <button
        type="button"
        title="Problem grouping needs review"
        aria-label="Problem grouping needs review"
        disabled={feedbackBusy}
        className="inline-flex items-center justify-center gap-1 rounded border px-1.5 py-1 text-[11px] font-medium transition-opacity hover:opacity-85 disabled:opacity-50"
        style={feedbackStyle(thumbsDownActive, palette.status.danger)}
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          onProblemFeedback?.(row, 'thumbs_down');
        }}
      >
        <ThumbsDown size={12} />
        <span>{feedback.thumbs_down || 0}</span>
      </button>
    </div>
  );
};

const ProblemStatusControl = ({ row, onResolveProblem, busyActionKey }) => {
  const problemId = row?.problem?.problem_id || row?.group?.problem_id || '';
  const resolved = row?.isResolved || row?.state === 'resolved';
  const resolveBusy = busyActionKey === `${row?.id}:resolve`;
  const statusText = resolved ? 'Resolved' : (resolveBusy ? 'Resolving' : 'Resolve');
  const toneColor = resolved ? palette.status.success : palette.text.supporting;

  if (!problemId) return <span className="text-xs" style={{ color: getTableCellColor('muted') }}>-</span>;

  if (resolved) {
    return (
      <span
        className="inline-flex items-center justify-center gap-1 rounded border px-2 py-1 text-[11px] font-medium"
        style={{ color: toneColor, borderColor: `${toneColor}55`, backgroundColor: `${toneColor}12` }}
      >
        <CheckCircle size={12} />
        {statusText}
      </span>
    );
  }

  return (
    <button
      type="button"
      title="Mark problem resolved"
      aria-label="Mark problem resolved"
      disabled={resolveBusy}
      className="inline-flex items-center justify-center rounded border px-2 py-1 text-[11px] font-medium transition-opacity hover:opacity-85 disabled:opacity-50"
      style={{ color: toneColor, borderColor: `${toneColor}55`, backgroundColor: `${toneColor}12` }}
      onClick={(event) => {
        event.preventDefault();
        event.stopPropagation();
        onResolveProblem?.(row);
      }}
    >
      {statusText}
    </button>
  );
};

const signalInfoSections = (row) => {
  const definition = row?.signalDefinition || {};
  return [
    { label: 'Signal', value: definition.signal?.name || row?.signalKey || '', description: definition.signal?.description || '' },
    { label: 'Type', value: definition.type?.name || '', description: definition.type?.description || '', key: definition.type?.key || '' },
    { label: 'Event', value: definition.event?.name || '', description: definition.event?.description || '', key: definition.event?.key || '' },
  ].filter(section => section.value || section.description || section.key);
};

const SignalInfoIcon = ({ row }) => {
  const [visible, setVisible] = useState(false);
  const sections = signalInfoSections(row);
  if (sections.length === 0) return null;

  const toggle = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setVisible(prev => !prev);
  };

  return (
    <span
      className="relative inline-flex shrink-0"
      onMouseEnter={() => setVisible(true)}
      onMouseLeave={() => setVisible(false)}
      onFocus={() => setVisible(true)}
      onBlur={() => setVisible(false)}
    >
      <span
        role="button"
        tabIndex={0}
        aria-label={`${row?.signal || 'Signal'} definition`}
        className="inline-flex items-center justify-center rounded-full"
        style={{ color: getTableCellColor('muted') }}
        onClick={toggle}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') toggle(event);
        }}
      >
        <Info size={13} />
      </span>
      {visible && (
        <span
          className="absolute left-0 top-5 z-50 flex w-[300px] flex-col gap-2 rounded-md border px-3 py-2 text-xs"
          style={{
            backgroundColor: palette.neutrals.panelSecondary,
            borderColor: palette.neutrals.borderStrong,
            color: palette.text.primary,
            boxShadow: palette.quality.shadowCard,
            lineHeight: 1.35,
          }}
        >
          {sections.map((section) => (
            <span key={`${section.label}:${section.value || section.key}`} className="block">
              <span className="mb-0.5 block text-[10px] font-semibold uppercase tracking-wider" style={{ color: palette.text.supporting }}>
                {section.label}
              </span>
              <span className="block font-medium" style={{ color: palette.text.primary }}>
                {section.value || section.key}
              </span>
              {section.description && (
                <span className="mt-0.5 block" style={{ color: palette.text.muted }}>
                  {section.description}
                </span>
              )}
              {section.key && section.key !== section.value && (
                <span className="mt-0.5 block font-mono text-[10px]" style={{ color: palette.text.supporting }}>
                  {section.key}
                </span>
              )}
            </span>
          ))}
        </span>
      )}
    </span>
  );
};

// Default row click delegates to the same handler its action button already
// uses — view_rca rows open/view the RCA, run_rca rows kick off analysis,
// and the rare view_traces fallback (error rows with no RCA data at all)
// opens the trace list. Keeps a single click behavior per row instead of
// requiring the user to land precisely on the action button.
const primaryRowHandler = (row, { onViewTraces, onViewRca, onRunRca }) => {
  if (row?.actionType === 'view_rca') return onViewRca;
  if (row?.actionType === 'run_rca') return onRunRca;
  if (row?.actionType === 'view_traces') return onViewTraces;
  return null;
};

const ActionableSignalsTable = ({
  rows = [],
  loading = false,
  error = null,
  warning = null,
  onViewTraces,
  onViewRca,
  onRunRca,
  onProblemFeedback,
  onResolveProblem,
  busyActionKey = '',
  selectedRowId = null,
}) => {
  const [hoveredRowId, setHoveredRowId] = useState(null);

  const sortedRows = useMemo(() => (
    [...(Array.isArray(rows) ? rows : [])]
      .filter(row => row && row.affected > 0)
      .sort((a, b) => {
        const priorityDelta = Number(b.priority || 0) - Number(a.priority || 0);
        if (priorityDelta !== 0) return priorityDelta;
        const affectedDelta = Number(b.affected || 0) - Number(a.affected || 0);
        if (affectedDelta !== 0) return affectedDelta;
        return `${a.signal || ''}`.localeCompare(`${b.signal || ''}`);
      })
  ), [rows]);

  return (
    <div
      className={`${components.card.bg} ${components.card.rounded} p-4`}
      style={{ border: `1px solid ${components.card.borderColor}` }}
    >
      <div className="mb-3 flex items-center justify-between gap-3">
        <h3 className={tablePanelTitleClassName} style={{ color: palette.text.supporting }}>
          Problems
        </h3>
        <span className="text-[11px]" style={{ color: palette.text.muted }}>
          Actionable findings, RCA coverage, and errors
        </span>
      </div>

      {loading ? (
        <div className="py-4 text-sm" style={{ color: palette.text.muted }}>
          Loading actionable signals...
        </div>
      ) : error ? (
        <div className="py-4 text-sm" style={{ color: palette.status.danger }}>
          {error}
        </div>
      ) : sortedRows.length === 0 ? (
        <div className="py-4 text-sm" style={{ color: palette.text.muted }}>
          No actionable signals found for this range.
        </div>
      ) : (
        <>
          {warning && (
            <div className="mb-3 rounded border px-3 py-2 text-xs" style={{ color: palette.status.warning, borderColor: `${palette.status.warning}55`, backgroundColor: `${palette.status.warning}10` }}>
              {warning}
            </div>
          )}
          <div className="overflow-visible rounded-md border" style={{ borderColor: components.card.borderColor }}>
            <div
              className={tableGridHeaderClassName}
              style={{
                gridTemplateColumns: gridTemplate,
                columnGap: gridGap,
              }}
            >
              <span>Signal</span>
              <span>Where</span>
              <span className="text-right">Count</span>
              <span>Evidence</span>
              <span>Root Cause</span>
              <span>Latest</span>
              <span>Traces</span>
              <span>Feedback</span>
              <span>Status</span>
            </div>

            {sortedRows.map((row, index) => {
              const rowHandler = primaryRowHandler(row, { onViewTraces, onViewRca, onRunRca });
              return (
              <div
                key={row.id}
                className={`grid items-center px-3 py-2.5 ${components.table.row.border} ${rowHandler ? 'cursor-pointer' : ''}`}
                style={getTableRowStyle(index, {
                  isHovered: hoveredRowId === row.id,
                  isSelected: selectedRowId != null && row.id === selectedRowId,
                  style: {
                    gridTemplateColumns: gridTemplate,
                    columnGap: gridGap,
                  },
                })}
                onMouseEnter={() => setHoveredRowId(row.id)}
                onMouseLeave={() => setHoveredRowId(null)}
                onClick={rowHandler ? () => rowHandler(row) : undefined}
              >
                {/* Column 1: Signal (name + description) */}
                <div className="min-w-0">
                  <div className="flex min-w-0 items-center gap-1.5">
                    <div className="truncate text-sm font-medium" style={{ color: getTableCellColor('entity') }} title={row.signal}>
                      {row.signal}
                    </div>
                    <SignalInfoIcon row={row} />
                  </div>
                  {row.signalDescription && (
                    <div className="truncate text-[11px]" style={{ color: getTableCellColor('muted') }} title={row.signalDescription}>
                      {row.signalDescription}
                    </div>
                  )}
                </div>

                {/* Column 2: Where */}
                <div className="min-w-0">
                  <div className="truncate text-xs" style={{ color: getTableCellColor('muted') }} title={row.where?.display}>
                    {row.where?.display || '-'}
                  </div>
                </div>

                {/* Column 3: Count */}
                <div className="text-right text-sm tabular-nums" style={{ color: getTableCellColor('metric') }}>
                  {formatCount(row.affected)}
                </div>

                {/* Column 4: Evidence */}
                <div className="min-w-0">
                  {row.evidence ? (
                    <div
                      className="overflow-hidden text-xs leading-5"
                      style={{
                        color: getTableCellColor('muted'),
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                      }}
                      title={row.evidence}
                    >
                      {row.evidence}
                    </div>
                  ) : (
                    <span className="text-xs" style={{ color: getTableCellColor('muted') }}>-</span>
                  )}
                </div>

                {/* Column 5: Root Cause */}
                <div className="min-w-0">
                  <div className="flex min-w-0 items-start gap-1">
                    <div
                      className="min-w-0 overflow-hidden text-xs leading-5"
                      style={{
                        color: getTableCellColor('muted'),
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        maxWidth: 'calc(100% - 26px)',
                      }}
                      title={row.finding}
                    >
                      {row.finding || '-'}
                    </div>
                    <RcaInlineAction
                      row={row}
                      onViewRca={onViewRca}
                      onRunRca={onRunRca}
                    />
                  </div>
                </div>

                {/* Column 6: Latest */}
                <div className="truncate text-xs" style={{ color: getTableCellColor('time') }} title={row.latestTitle || row.latest}>
                  {row.latest || '-'}
                </div>

                {/* Column 7: Traces */}
                <div className="flex justify-start">
                  <TracesButton row={row} onViewTraces={onViewTraces} />
                </div>

                {/* Column 8: Feedback */}
                <div className="flex justify-start">
                  <ProblemFeedbackButtons
                    row={row}
                    onProblemFeedback={onProblemFeedback}
                    busyActionKey={busyActionKey}
                  />
                </div>

                {/* Column 9: Status */}
                <div className="flex justify-start">
                  <ProblemStatusControl
                    row={row}
                    onResolveProblem={onResolveProblem}
                    busyActionKey={busyActionKey}
                  />
                </div>
              </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

export default ActionableSignalsTable;
